import React, { useState } from "react";
import {
  Box,
  Button,
  Code,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { AiPromptPresetPicker } from "./AiPromptPresetPicker";
import { generateAiText } from "../aiApi";
import { ListingEntityType, GenerateTextOutput } from "./types/ai.types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  entityType: ListingEntityType;
  entityId: string;
  title?: string;
  description?: string;
  price?: number;
  location?: string;
  initialFields?: Record<string, unknown>;
  onApply?: (result: GenerateTextOutput) => void;
};

export const AiTextGeneratorModal: React.FC<Props> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  title,
  description,
  price,
  location,
  initialFields,
  onApply,
}) => {
  const toast = useToast();

  const [presetKey, setPresetKey] = useState("product-marketplace-description");
  const [customInstructions, setCustomInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GenerateTextOutput | null>(null);

  const modalBg = useColorModeValue("white", "gray.900");
  const sectionBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const outputBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const bodyTextColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const inputBg = useColorModeValue("white", "gray.800");
  const inputTextColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const inputBorderColor = useColorModeValue("gray.200", "gray.600");
  const inputHoverBorderColor = useColorModeValue("teal.400", "teal.300");
  const inputFocusBorderColor = useColorModeValue("teal.500", "teal.300");
  const inputDisabledBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const inputInvalidBorderColor = useColorModeValue("red.400", "red.300");
  const placeholderColor = useColorModeValue("gray.400", "gray.500");

  const ghostButtonHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const codeBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const codeColor = useColorModeValue("teal.700", "teal.200");

  const sharedInputStyles = {
    bg: inputBg,
    color: inputTextColor,
    borderColor: inputBorderColor,
    borderRadius: "xl",
    transition: "all 0.18s ease",
    _placeholder: {
      color: placeholderColor,
    },
    _hover: {
      borderColor: inputHoverBorderColor,
    },
    _focus: {
      borderColor: inputFocusBorderColor,
      boxShadow: `0 0 0 1px ${inputFocusBorderColor}`,
    },
    _focusVisible: {
      borderColor: inputFocusBorderColor,
      boxShadow: `0 0 0 1px ${inputFocusBorderColor}`,
    },
    _invalid: {
      borderColor: inputInvalidBorderColor,
      boxShadow: `0 0 0 1px ${inputInvalidBorderColor}`,
    },
    _disabled: {
      bg: inputDisabledBg,
      opacity: 0.75,
      cursor: "not-allowed",
    },
    sx: {
      "&:-webkit-autofill": {
        boxShadow: `0 0 0px 1000px ${inputBg} inset`,
        WebkitTextFillColor: inputTextColor,
        caretColor: inputTextColor,
        transition: "background-color 9999s ease-in-out 0s",
      },
    },
  };

  const handleGenerate = async () => {
    setLoading(true);

    try {
      const response = await generateAiText({
        entityType,
        entityId,
        provider: "openai",
        presetKey,
        customInstructions,
        fields: {
          ...initialFields,
          title,
          description,
          price,
          location,
        },
      });

      setResult(response);

      toast({
        title: "AI text generated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "AI text generation failed",
        description: error?.message || "Please check backend logs.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const displayRows = [
    ["Title", result?.pickDenguTitle || result?.marketplaceTitle || result?.title],
    ["Short description", result?.shortDescription],
    ["Full description", result?.fullDescription || result?.description],
    ["Facebook Marketplace", result?.facebookMarketplaceDescription],
    ["Instagram", result?.instagramCaption],
    ["TikTok", result?.tiktokCaption],
    ["WhatsApp", result?.whatsappReplyMessage || result?.whatsappReply],
    ["CTA", result?.safeCheckoutCta || result?.ctaText],
  ].filter(([, value]) => Boolean(value));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />

      <ModalContent
        bg={modalBg}
        color={bodyTextColor}
        borderRadius="2xl"
        overflow="hidden"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
          <Box>
            <Text color={headingColor}>✨ Generate AI Listing Content</Text>
            <Text fontSize="sm" color={mutedText} fontWeight="normal" mt={1}>
              Create PickDengu-ready descriptions, captions and WhatsApp replies.
            </Text>
          </Box>
        </ModalHeader>

        <ModalCloseButton
          color={mutedText}
          _hover={{ bg: ghostButtonHoverBg, color: headingColor }}
        />

        <ModalBody py={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <VStack align="stretch" spacing={4}>
              <AiPromptPresetPicker
                entityType={entityType}
                category="description"
                value={presetKey}
                onChange={setPresetKey}
              />

              <FormControl>
                <FormLabel fontSize="sm" color={headingColor}>
                  Extra instructions
                </FormLabel>

                <Textarea
                  value={customInstructions}
                  onChange={(event) => setCustomInstructions(event.target.value)}
                  placeholder="Example: Make it premium, short, Malawi-friendly, and mention safe checkout."
                  minH="120px"
                  resize="vertical"
                  {...sharedInputStyles}
                />
              </FormControl>

              <Box
                p={4}
                bg={sectionBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
              >
                <Text fontWeight="semibold" mb={2} color={headingColor}>
                  Current listing context
                </Text>

                <Text fontSize="sm" color={mutedText}>
                  {title || "Untitled listing"}
                </Text>
                <Text fontSize="sm" color={mutedText}>
                  {location || "Malawi"}{" "}
                  {price ? `• MWK ${price.toLocaleString()}` : ""}
                </Text>
              </Box>
            </VStack>

            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Text fontWeight="semibold" color={headingColor}>
                  Generated output
                </Text>

                <Button
                  size="sm"
                  colorScheme="teal"
                  onClick={handleGenerate}
                  isLoading={loading}
                >
                  Generate
                </Button>
              </HStack>

              <Box
                minH="360px"
                p={4}
                bg={outputBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                overflowY="auto"
              >
                {result ? (
                  <VStack align="stretch" spacing={4}>
                    {displayRows.map(([label, value]) => (
                      <Box key={label as string}>
                        <Text
                          fontSize="xs"
                          color={mutedText}
                          fontWeight="bold"
                          mb={1}
                        >
                          {label}
                        </Text>
                        <Text
                          fontSize="sm"
                          whiteSpace="pre-wrap"
                          color={bodyTextColor}
                        >
                          {String(value)}
                        </Text>
                      </Box>
                    ))}

                    {Array.isArray(result.hashtags) && result.hashtags.length > 0 && (
                      <>
                        <Divider borderColor={borderColor} />
                        <Text fontSize="xs" color={mutedText} fontWeight="bold">
                          Hashtags
                        </Text>
                        <Code
                          whiteSpace="normal"
                          p={2}
                          borderRadius="lg"
                          bg={codeBg}
                          color={codeColor}
                        >
                          {result.hashtags.join(" ")}
                        </Code>
                      </>
                    )}
                  </VStack>
                ) : (
                  <Text color={mutedText} fontSize="sm">
                    Click Generate to preview AI content here.
                  </Text>
                )}
              </Box>
            </VStack>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={onClose}
              color={bodyTextColor}
              _hover={{ bg: ghostButtonHoverBg }}
            >
              Cancel
            </Button>

            <Button
              colorScheme="teal"
              isDisabled={!result}
              onClick={() => result && onApply?.(result)}
            >
              Apply to listing
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};